import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BubzLogo } from "../components/BubzLogo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/check/${token}`).then(({ data }) => setInvite(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail) || "This invite link isn't valid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register-invite", { token, ...form, accept_terms: agreed });
      setToken(data.token);
      setUser(data);
      toast.success(`Welcome to Bubz +, ${data.name.split(" ")[0]}! 🎉`);
      navigate("/profiles", { replace: true });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center px-6 py-12" data-testid="invite-accept-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-amber-100 shadow-xl p-8">
        <BubzLogo size="lg" />
        {error ? (
          <div className="mt-6" data-testid="invite-error">
            <h1 className="text-2xl font-display font-bold text-slate-900">Invite not available</h1>
            <p className="text-slate-500 mt-2">{error}</p>
            <Link to="/" className="inline-block mt-6 font-bold text-[#9B5DE5] hover:underline" data-testid="invite-go-login">Go to sign in</Link>
          </div>
        ) : !invite ? (
          <p className="mt-6 text-slate-400">Checking your invite…</p>
        ) : (
          <>
            <h1 className="mt-6 text-3xl font-display font-bold text-slate-900">You're invited! 🎉</h1>
            <p className="text-slate-500 mt-1"><strong>{invite.invited_by_name}</strong> invited you to join Bubz +.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="invite-form">
              <div>
                <Label>Email</Label>
                <Input value={invite.email} disabled data-testid="invite-email" className="mt-1 rounded-xl h-12 disabled:opacity-100 bg-slate-50 text-slate-700 font-semibold" />
              </div>
              <div>
                <Label htmlFor="inv-name">Parent name</Label>
                <Input id="inv-name" required data-testid="invite-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl h-12" placeholder="Sophie Parker" />
              </div>
              <div>
                <Label htmlFor="inv-pass">Password</Label>
                <Input id="inv-pass" type="password" required minLength={6} data-testid="invite-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl h-12" placeholder="At least 6 characters" />
              </div>
              <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer" data-testid="invite-terms-label">
                <Checkbox data-testid="invite-terms-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
                <span>I am 18+ and agree to the{" "}
                  <Link to="/terms" target="_blank" className="font-bold text-[#9B5DE5] hover:underline">Terms &amp; Conditions</Link> and{" "}
                  <Link to="/privacy" target="_blank" className="font-bold text-[#9B5DE5] hover:underline">Privacy Policy</Link>, including parental consent for my kids' profiles.
                </span>
              </label>
              <Button type="submit" disabled={busy || !agreed} data-testid="invite-submit-btn"
                className="w-full h-12 rounded-xl text-base font-bold bg-[#FF5A79] hover:bg-[#e04865]">
                {busy ? "Creating…" : "Create my account"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
